'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListingImage } from '@kit/marketplace/types';
import { cdnImageUrl } from '@kit/shared/storage';
import { ImageGallery, type ViewerImage } from '@kit/ui/image-viewer';

interface ListingImageGalleryProps {
  /** Pre-resolved image URLs in display_order. */
  images: Array<{ image: ListingImage; url: string }>;
  title: string;
}

/**
 * Inline thumbnails + hero with click-to-zoom lightbox, powered by the shared
 * `@kit/ui` ImageGallery (zoom, pan, keyboard nav, immersive fullscreen).
 *
 * Maps marketplace `ListingImage` rows onto the storage-decoupled `ViewerImage`
 * shape; CDN transforms are resolved here, the viewer stays storage-agnostic.
 */
export function ListingImageGallery({
  images,
  title,
}: ListingImageGalleryProps) {
  const { t } = useTranslation();

  const viewerImages = useMemo<ViewerImage[]>(
    () =>
      images.map(({ image, url }, idx) => ({
        id: image.id,
        src: cdnImageUrl(url, 'HERO') ?? url,
        thumbnailSrc: cdnImageUrl(url, 'THUMBNAIL') ?? url,
        alt: t('explore:imageGallery.imageAlt', { title, number: idx + 1 }),
        fileName: image.file_name ?? undefined,
        fileSize: image.file_size ?? undefined,
        downloadUrl: url,
      })),
    [images, title, t],
  );

  const translations = useMemo(
    () => ({
      close: t('common:imageViewer.close'),
      previous: t('common:imageViewer.previous'),
      next: t('common:imageViewer.next'),
      zoomIn: t('common:imageViewer.zoomIn'),
      zoomOut: t('common:imageViewer.zoomOut'),
      resetZoom: t('common:imageViewer.resetZoom'),
      fullscreen: t('common:imageViewer.fullscreen'),
      exitFullscreen: t('common:imageViewer.exitFullscreen'),
      download: t('common:imageViewer.download'),
      noImage: t('explore:imageGallery.noImage'),
    }),
    [t],
  );

  return (
    <div data-testid="listing-image-gallery">
      <ImageGallery images={viewerImages} translations={translations} />
    </div>
  );
}
